import React, { useContext, useEffect, useRef, useState } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { python } from '@codemirror/lang-python'
import { AuthContext } from '../../context/AuthContext'
import { showToast } from '../commonUnit/Toast'
import { githubLight } from '@uiw/codemirror-theme-github'
import { updateTestCodeInSession } from '../../service/testService'
import { SessionContext } from '../../context/SessionContext'
import { indentUnit } from '@codemirror/language'
import { Typography, Button } from '@mui/material'
import { SelectedCodeContext } from '../../context/SelectedCodeContext'
import { addCodeAndUpdateUser, getCodeById } from "../../service/codeService"
import BlockpyMirror from '../code/BlockpyMirror';
import { validateTests } from "../../tool/validateTests"
import '../../css/studentview.scss'

// The editors' placeholder defaults. Saved as-is they are junk - and '#Unit Test' is a
// preprocessor error in a C++ harness - so a save that still holds one writes '' instead.
const STARTER_PLACEHOLDER = '#StarterCode'
const UNIT_TEST_PLACEHOLDER = '#Unit Test'
const unplaceholder = (value, placeholder) => (value === placeholder ? '' : value)
const CLEAN_FIELDS = { starter: false, template: false, unit_test: false }
// BlockpyMirror mounts by document.getElementById, so its id must be unique on the page.
// The Monitor tab is kept mounted and its student-code viewer (CodeArea) already uses
// 'blockmirror-editor'; sharing that id attached this editor to Monitor's hidden div.

// Optional props, both defaulting to today's rendering for the existing mounts
// (Task-UnitTest-Panel, UnitTest, the wizard's Code Stubs step):
//   hideHarness  hide Variable Setup + Unit Test - for exercises with test cases turned off,
//                where the harness is never run.
//   variant      'studentPane': the Session Setting tab's edit panel for the student code
//                template, with Cancel / Save (see below). 'harness': only Variable Setup +
//                Unit Test, for that tab's test-case dialog. Two instances can share a page
//                safely: a save writes this instance's edited fields and the server's
//                current value for the rest (valueToSave), then publishes the write.
//   onDone       studentPane only: called after Cancel or a successful Save.
//   onTemplateDraftChange  reports the Student Code Template as it is typed, saved or not -
//                the creation wizard's live student preview shows it immediately.
const StarterCode = ({ hideHarness = false, variant = 'default', onDone = null, onTemplateDraftChange = null }) => {
  const { currentUser } = useContext(AuthContext)
  const { rerender,setRerender } = useContext(SelectedCodeContext)
  const [starterCode, setStarterCode] = useState(STARTER_PLACEHOLDER)
  const [unitTest, setUnitTest] = useState(UNIT_TEST_PLACEHOLDER)
  const [templateCode, setTemplateCode] = useState('')
  const { session, setSession } = useContext(SessionContext)
  // Latest session for callbacks registered once and kept (BlockpyMirror only re-binds its
  // change listener when the code text changes, so its onCodeChange can hold a session from
  // many deliveries ago - and write that stale harness back).
  const sessionRef = useRef(session)
  sessionRef.current = session
  // Which session the editors were last seeded from, and which editors hold edits that have
  // not been saved yet. SessionContext re-delivers the session every 10s (poll/websocket);
  // re-seeding unconditionally on each delivery overwrote whatever the instructor was
  // typing. Tracked per field: an editor with unsaved edits keeps them, every other editor
  // still takes the incoming value - so a harness regenerated by TestList shows up here
  // even while the template is being edited. A different session resets everything.
  const seededForRef = useRef(null)
  const dirtyRef = useRef({ ...CLEAN_FIELDS })
  // Bumped on every edit. A save only clears the dirty flags if no edit arrived while it
  // was in flight; otherwise the push from that save would overwrite the newer keystrokes.
  const editGenerationRef = useRef(0)
  // The save currently in flight, if any. "Generate AI Solution" waits for it so the two
  // writes cannot race (the button's mousedown blurs the schema editor, which saves).
  const inFlightSaveRef = useRef(null)

  useEffect(() => {
    console.log(session)
    if (!session || !session.test_code) return
    if (seededForRef.current !== session.id) {
      seededForRef.current = session.id
      dirtyRef.current = { ...CLEAN_FIELDS }
    }
    const dirty = dirtyRef.current
    if (!dirty.starter) {
      if(session.type.startsWith("SQL")){
        getCodeById(session.test_code.starter).then((code)=>{
          console.log(code)
          if(code!=null && !dirtyRef.current.starter){
            setStarterCode(code.content)
          }
        })
      }
      else{
        setStarterCode(session.test_code.starter)
      }
    }
    
    if (!dirty.template) setTemplateCode(session.test_code.template)
    if (!dirty.unit_test) setUnitTest(session.test_code.unit_test)
  }, [session])

  useEffect(() => {
    if (onTemplateDraftChange) onTemplateDraftChange(templateCode)
  }, [templateCode]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!session) return null

  const markDirty = (field) => {
    dirtyRef.current = { ...dirtyRef.current, [field]: true }
    editGenerationRef.current += 1
  }
  const hasDirtyField = () => Object.values(dirtyRef.current).some(Boolean)

  const handleStarterCodeChange = (e) => {
    markDirty('starter')
    setStarterCode(e)
  }

  const handleTestChange = (e) => {
    markDirty('unit_test')
    setUnitTest(e)
  }

  const handleTemplateChange = (e) => {
    markDirty('template')
    setTemplateCode(e)
  }

  // Blur used to save unconditionally - including the untouched '#StarterCode' /
  // '#Unit Test' placeholders into a session whose test_code was still empty. Only save
  // when something was actually edited; the explicit Save button is unchanged.
  const saveIfEdited = (e) => {
    if (hasDirtyField()) handleUpdateClick(e)
  }

  // Every save writes all three fields. For a field this editor has not touched, send the
  // server's current value rather than local state, so saving the template can never
  // revert a harness that was regenerated after this editor last seeded.
  const valueToSave = (field, localValue) =>
    dirtyRef.current[field] || session.test_code?.[field] === undefined
      ? localValue
      : session.test_code[field]

  const clearDirtyIfUnchangedSince = (generation) => {
    if (editGenerationRef.current === generation) dirtyRef.current = { ...CLEAN_FIELDS }
  }

  // After a successful write, put it into SessionContext straight away rather than waiting
  // for the websocket push / 10s poll. Until then the context still held the OLD test_code,
  // so a second save in that window (the Save button, or blurring another editor) sent the
  // old value for every clean field and reverted what was just saved.
  const publishTestCode = (sessionId, payload) => {
    setSession?.((prev) =>
      prev && prev.id === sessionId ? { ...prev, test_code: { ...(prev.test_code || {}), ...payload } } : prev
    )
  }

  // Blockly writes the template on every block change, bypassing the editors above.
  const saveBlocklyTemplate = (code) => {
    const current = sessionRef.current
    setTemplateCode(code)
    const payload = {
      starter: current.test_code?.starter,
      template: code,
      unit_test: current.test_code?.unit_test,
    }
    updateTestCodeInSession(current.id, payload).then((ok) => {
      if (ok) publishTestCode(current.id, payload)
    })
  }

  // SQL: ask the model for the reference query and store it as the unit test. The payload
  // is built only after the (slow) model call AND any in-flight blur-save have finished,
  // from the latest session - building it up front from the render-time session pointed
  // test_code.starter back at the old schema row, undoing a schema saved meanwhile. The
  // result is also published and shown, so a later save cannot send the old unit_test.
  const generateCode = async (e) => {
    let result = await validateTests(sessionRef.current.task,"sql")
    console.log(result)
    if (inFlightSaveRef.current) {
      try { await inFlightSaveRef.current } catch (error) { /* reported by that save */ }
    }
    const current = sessionRef.current
    const payload = {
      starter: current.test_code?.starter,
      template: current.test_code?.template ?? templateCode,
      unit_test: result,
    }
    let log = await updateTestCodeInSession(current.id, payload)
    console.log(log)
    if (log) {
      publishTestCode(current.id, payload)
      setUnitTest(result)
      dirtyRef.current = { ...dirtyRef.current, unit_test: false }
    }
  }

  const handleUpdateClick = (e) => {
    const save = runUpdate(e)
    inFlightSaveRef.current = save
    return save.finally(() => {
      if (inFlightSaveRef.current === save) inFlightSaveRef.current = null
    })
  }

  const runUpdate = async (e) => {
    console.log(e)
    e?.preventDefault()
    console.log(templateCode)
    const generationAtStart = editGenerationRef.current
    if(session.type.startsWith("SQL")){
      // The SQL starter is a code-row id. Only store a new schema row when the schema was
      // actually edited (or there is no row yet) - saving the Unit Test alone used to
      // re-point the session at a copy of whatever the editor held, including the
      // '#StarterCode' placeholder before the real schema had loaded.
      let starterId = session.test_code?.starter
      if (dirtyRef.current.starter || starterId == null) {
        const result = await addCodeAndUpdateUser(
          unplaceholder(starterCode, STARTER_PLACEHOLDER),
          0,
          currentUser.id,
          null
        )
        console.log(result)
        starterId = result?.code?.id ?? starterId
      }
      const payload = {
        starter: starterId,
        template: valueToSave('template', templateCode),
        unit_test: valueToSave('unit_test', unplaceholder(unitTest, UNIT_TEST_PLACEHOLDER)),
      }
      const success = await updateTestCodeInSession(session.id, payload)
      console.log(success)
  
      if (success) {
        publishTestCode(session.id, payload)
        clearDirtyIfUnchangedSince(generationAtStart)
        showToast('Test updated successfully.', 'success')
      } else {
        showToast('Failed to update starter code.', 'error')
      }
      return success
    }
    const payload = {
      starter: valueToSave('starter', unplaceholder(starterCode, STARTER_PLACEHOLDER)),
      template: valueToSave('template', templateCode),
      unit_test: valueToSave('unit_test', unplaceholder(unitTest, UNIT_TEST_PLACEHOLDER)),
    }
    const success = await updateTestCodeInSession(session.id, payload)
    console.log(success)

    if (success) {
      publishTestCode(session.id, payload)
      clearDirtyIfUnchangedSince(generationAtStart)
      showToast('Test updated successfully.', 'success')
    } else {
      showToast('Failed to update starter code.', 'error')
    }
    return success
  }

  const canEdit = !(currentUser?.role > 1)

  // variant="studentPane": the Session Setting tab's edit panel for the student code
  // template - the code every student's editor starts with. It only exists while the
  // instructor is editing (the tab shows a read-only student preview otherwise), so it
  // saves on an explicit Save and discards on Cancel; onDone returns to the preview.
  // Blockly writes on every block change (BlockpyMirror has no draft state), so it gets a
  // single Done instead.
  if (variant === 'studentPane' && !session.type.startsWith("SQL")) {
    const isBlockly = session?.type?.startsWith("Blockly")
    const handleCancel = () => {
      setTemplateCode(session.test_code?.template ?? '')
      dirtyRef.current = { ...dirtyRef.current, template: false }
      onDone?.()
    }
    const handleSave = async (e) => {
      const saved = await handleUpdateClick(e)
      if (saved) onDone?.()
    }
    return (
      <div className="starter-pane">
        <div className="starter-pane__head">
          <span className="starter-pane__title">Student code template</span>
          <span className="starter-pane__hint">
            {isBlockly ? 'Block changes save as you make them' : 'What every student\u2019s editor starts with'}
          </span>
          {isBlockly ? (
            <Button size="small" variant="contained" onClick={() => onDone?.()}>
              Done
            </Button>
          ) : (
            <>
              <Button size="small" onClick={handleCancel}>
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={handleSave} disabled={!canEdit}>
                Save
              </Button>
            </>
          )}
        </div>
        <div className="starter-pane__editor">
          {isBlockly ? (
            <span key={rerender}>
              <BlockpyMirror
                currentCode={templateCode}
                onCodeChange={(code) => saveBlocklyTemplate(code)}
                configuration={{
                  'toolbox': "ct2",
                  "blocklyMediaPath": "../lib/blockly/media/",
                  "viewMode": 'block',
                  "height": 300
                }}
                divID={'blockmirror-template-editor'}
              />
            </span>
          ) : (
            <CodeMirror
              value={templateCode}
              theme={githubLight}
              extensions={[python(), indentUnit.of('    ')]}
              height="100%"
              style={{ height: '100%' }}
              onChange={handleTemplateChange}
              editable={canEdit}
              autoFocus
              placeholder="# Code students start from"
            />
          )}
        </div>
      </div>
    )
  }

  // variant="harness": just the grading harness (Variable Setup + Unit Test), for the
  // Session Setting tab's test-case dialog. Saves on blur like the classic editor, next to
  // TestList, which also saves as you go.
  if (variant === 'harness' && !session.type.startsWith("SQL")) {
    return (
      <div className="starter-harness">
        <div className="starter-pane__label">Variable Setup</div>
        <CodeMirror
          extensions={[python(), indentUnit.of('    ')]}
          theme={githubLight}
          value={starterCode}
          height="120px"
          onChange={handleStarterCodeChange}
          onBlur={saveIfEdited}
          editable={canEdit}
        />
        <div className="starter-pane__label">Unit Test</div>
        <CodeMirror
          value={unitTest}
          theme={githubLight}
          extensions={[python(), indentUnit.of('    ')]}
          height="180px"
          onChange={handleTestChange}
          onBlur={saveIfEdited}
          editable={canEdit}
        />
      </div>
    )
  }

  return (
    <div className="scafold">
      <Typography variant="h6" fontWeight={'light'} gutterBottom>
        Unit Test
      </Typography>
      <div className="topbar">
        {session.test_code &&
        (starterCode === session.test_code.starter||session.type.startsWith("SQL") )&&
        templateCode === session.test_code.template &&
        unitTest === session.test_code.unit_test ? (
          <></>
        ) : (
          <button className="codeButton" onClick={handleUpdateClick}>
            Save
          </button>
        )}
      </div>
      {/* SQL's "Variable Setup" is the schema students query, not a test harness, so it
          stays visible even with hideHarness. */}
      {(!hideHarness || session.type.startsWith("SQL")) && (
        <>
      Variable Setup
      <CodeMirror
        extensions={[python(), indentUnit.of('    ')]}
        theme={githubLight}
        value={starterCode}
        height={currentUser?.role > 1 ? '100px' : (session.type.startsWith("SQL")?'50vh':'200px')}
        onChange={handleStarterCodeChange}
        onBlur={saveIfEdited}
        editable={currentUser?.role > 1 ? false : true}/>
        </>
      )}
      
      {!hideHarness && (
      <div style={{marginBottom:"40px"}}>
      Unit Test
      <CodeMirror
        value={unitTest}
        theme={githubLight}
        extensions={[python(), indentUnit.of('    ')]}
        onChange={handleTestChange}
        onBlur={saveIfEdited}
        editable={currentUser?.role > 1 ? false : true}
        
      />
      </div>
      )}
      {!session.type.startsWith("SQL") && (
        <>
        Student Code Template
        {session?.type?.startsWith("Blockly") && (
          <span key={rerender}>
            <BlockpyMirror
            currentCode={templateCode}
            onCodeChange={(code) => saveBlocklyTemplate(code)}
            configuration={{
              'toolbox':"ct2",
              "blocklyMediaPath": "../lib/blockly/media/",
              "viewMode": 'block',
              "height": 300
            }}
            divID={'blockmirror-template-editor'}
            />
          </span>
        )}
        {!session?.type?.startsWith("Blockly") && (
          <CodeMirror
          value={templateCode}
          theme={githubLight}
          extensions={[python(), indentUnit.of('    ')]}
          height={currentUser?.role > 1 ? '100px' : '200px'}
          onChange={handleTemplateChange}
          onBlur={saveIfEdited}
          editable={currentUser?.role > 1 ? false : true}
        />
        )}
        
        
        </>
      )}
      {session.type.startsWith("SQL") && (
          <>
          <Button
            variant="contained"
            onClick={(e) => {
              generateCode(e);
            }}
          >
            Generate AI Solution
          </Button>
          </>
        )}
    </div>
  )
}
export default StarterCode
